import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import {
    getMyRegistrationCodeCacheKey,
    getPaymentStatusCacheKey,
    getRegistrationCodeValidationCacheKey,
    getWebProductsCacheKey,
    onResetPaymentStatus,
} from './cacheKeys';
import { useMemo } from 'react';
import useQueryCallbacks, { useQueryErrorNotify } from '../useQueryCallbacks';
import React from 'react';
import { useExplicitAppType } from '../useAppType';
import Purchases from 'react-native-purchases';
import { Linking, Platform } from 'react-native';
import waitForAppStateRefocus from '../../utils/waitForAppStateRefocus';

const intl = getIntl('usePayments', {
    errorCreatingCheckoutSession: 'Error occurred creating checkout session',
    errorCreatingBillingPortalSession: 'Error occurred creating billing session',
    errorLoadingProducts: 'Error occurred loading products',
    errorValidatingPromoCode: 'Error occurred validating promo code',
    errorLoadingPaymentStatus: 'Error occurred loading payment status',
    errorApplyingPromoCode: 'Error occurred applying promo code',
    errorLoadingRegistrationCodeStatus: 'Error occurred loading registration code status',
    errorDeletingRegistrationCode: 'Error occurred deleting registration code',
    successDeletingRegistrationCode: 'Registration code deleted',
    autoApplyRegistrationCodeFailure: 'Your registration code could not be applied',
    successfullyPurchased: 'Successfully purchased',
    errorPurchase: 'Error occurred purchasing',
});

export type PurchaseMobileProductResult = 'purchased' | 'not-purchased';

export type UsePaymentArgs = {
    onReceivedCheckoutSessionUrl?: (url: string) => void;
    onReceiveBillingPortalSessionUrl?: (url: string) => void;
    onRegistrationCodeApplied?: (code: string) => void;
    registrationCodeValidationInput?: string;
    pollForPaymentStatus?: boolean;
    onAutoApplyRegistrationCodeSuccess?: (code: string) => void;
    onMaybePurchaseNativeProductProduct?: (result: PurchaseMobileProductResult) => void;
    onCompletedNativeProductManagementSession?: () => void;
};

export type ProfileManagementEntitlements = Exclude<APITypes.paymentService.APIEntitlement, 'Setup and Training'>;

export type PurchaseNativeProductRequest = Pick<APITypes.paymentService.WebCheckoutSessionRequest, 'productType'>;

export type UsePaymentReturnValue = {
    isLoadingConnectUrl: boolean;
    createWebCheckoutSession: (request: APITypes.paymentService.WebCheckoutSessionRequest) => void;
    isLoadingBillingPortalUrl: boolean;
    createWebBillingPortalSession: (request: APITypes.paymentService.WebBillingSessionRequest) => void;
    isLoadingWebProducts: boolean;
    paymentStatus: APITypes.paymentService.PaymentStatusResponse | null;
    isLoadingPaymentStatus: boolean;
    resetPaymentStatus: () => void;
    isValidatingRegistrationCode: boolean;
    registrationCodeValidationResult: APITypes.paymentService.ValidateRegistrationCodeResponse | null;
    webProducts: APITypes.paymentService.WebProductListItem[];
    myMostPermissiveEntitlement: ProfileManagementEntitlements | null;
    applyRegistrationCode: (request: APITypes.paymentService.ApplyRegistrationCodeRequest) => void;
    isApplyingRegistrationCode: boolean;
    myRegistrationCodeStatus: APITypes.paymentService.UserRegistrationCodeStatus | null;
    isLoadingRegistrationCodeStatus: boolean;
    deleteRegistrationCode: () => void;
    isDeletingRegistrationCode: boolean;
    autoApplyRegistrationCode: (request: APITypes.paymentService.ApplyRegistrationCodeRequest) => void;
    isAutoApplyingRegistrationCode: boolean;
    purchaseNativeProduct: (request: PurchaseNativeProductRequest) => void;
    isPurchasingNativeProduct: boolean;
    createNativeProductManagementSession: () => void;
    isLoadingNativeProductManagementSession: boolean;
    isDelayingForNativeWebhookPropagation: boolean;
};

const getPurchasesStoreProduct = async (productType: 'family-plan-monthly' | 'family-plan-yearly') => {
    if (Platform.OS === 'ios') {
        const targetProductId = {
            'family-plan-monthly': 'MonthlyFamily',
            'family-plan-yearly': 'YearlyFamily',
        }[productType];

        const products = await Purchases.getProducts([targetProductId]);

        if (products?.length !== 1) {
            throw new Error('Product not found');
        }

        return products[0];
    } else if (Platform.OS === 'android') {
        // for whatever reason, you need to search the product group ID for android...
        const products = await Purchases.getProducts(['pr_family_v1']);
        const targetProductId = {
            'family-plan-monthly': 'pr_family_v1:monthly-autorenewing',
            'family-plan-yearly': 'pr_family_v1:annual-autorenewing',
        }[productType];
        const product = products?.find((product) => product.identifier === targetProductId);
        if (!product) {
            throw new Error('Product not found');
        }

        return product;
    } else {
        throw new Error('Unsupported platform for native purchases');
    }
};

async function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function usePayments({
    onReceivedCheckoutSessionUrl,
    onReceiveBillingPortalSessionUrl,
    registrationCodeValidationInput,
    onRegistrationCodeApplied,
    pollForPaymentStatus,
    onAutoApplyRegistrationCodeSuccess,
    onMaybePurchaseNativeProductProduct: onPurchaseMobileProduct,
    onCompletedNativeProductManagementSession: onCompletedMobileProductManagementSession,
}: UsePaymentArgs): UsePaymentReturnValue {
    const enableQueries = useExplicitAppType() === 'consumer';
    const notify = useNotify();
    const [paymentStatusPollCount, setPaymentStatusPollCount] = React.useState(0);
    const [isAwaitingNativeWebhookPropagation, setIsAwaitingNativeWebhookPropagation] = React.useState(false);
    /**
     * Payment requires revenue cat webhook propagation. This can take some time.
     * Add a delay but also return a flag to give a more helpful message to the user.
     */
    const awaitNativeWebhookPropagation = async () => {
        setIsAwaitingNativeWebhookPropagation(true);
        await sleep(8000);
        setIsAwaitingNativeWebhookPropagation(false);
    };

    const purchaseNativeProductMutation = useMutation({
        mutationFn: async (request: PurchaseNativeProductRequest): Promise<PurchaseMobileProductResult> => {
            if (Platform.OS !== 'ios' && Platform.OS !== 'android') {
                throw new Error('Unsupported platform for native purchases');
            }

            if (request.productType !== 'family-plan-monthly' && request.productType !== 'family-plan-yearly') {
                throw new Error('Unsupported product type for native payments');
            }

            const product = await getPurchasesStoreProduct(request.productType);
            try {
                await Purchases.purchaseStoreProduct(product);
                await awaitNativeWebhookPropagation();
                onResetPaymentStatus.resetCache({});
                return 'purchased';
            } catch (error) {
                console.error(error);
                return 'not-purchased';
            }
        },
        onSuccess: (data) => {
            onPurchaseMobileProduct?.(data);
        },
        onError: (error) => {
            console.error(error);
            notify('error', intl('errorPurchase'));
        },
    });

    const createCheckoutSessionMutation = useMutation({
        mutationFn: async (request: APITypes.paymentService.WebCheckoutSessionRequest) => {
            const response = await appAPI.post<APITypes.paymentService.WebCheckoutSessionResponse>(
                urls.api.payment.webCheckoutSession,
                {
                    body: request,
                },
            );
            return response.url;
        },
        onSuccess: (data) => {
            onReceivedCheckoutSessionUrl?.(data as string);
        },
        onError: (error) => {
            console.error(error);
            notify('error', intl('errorCreatingCheckoutSession'));
        },
    });

    const createNativeProductManagementSessionMutation = useMutation({
        mutationFn: async () => {
            if (Platform.OS !== 'ios' && Platform.OS !== 'android') {
                throw new Error('Unsupported platform for native product management');
            }

            const customerInfo = await Purchases.getCustomerInfo();
            if (!customerInfo) {
                throw new Error('No customer info found');
            }

            if (!customerInfo.managementURL) {
                throw new Error('No management URL found');
            }

            const refocusPromise = waitForAppStateRefocus();
            Linking.openURL(customerInfo.managementURL);
            await refocusPromise;
            await awaitNativeWebhookPropagation();
            onResetPaymentStatus.resetCache({});
        },
        onSuccess: () => {
            onCompletedMobileProductManagementSession?.();
        },
        onError: (error) => {
            console.error(error);
            notify('error', intl('errorCreatingBillingPortalSession'));
        },
    });

    const createBillingPortalSessionMutation = useMutation({
        mutationFn: async (request: APITypes.paymentService.WebBillingSessionRequest) => {
            const response = await appAPI.post<APITypes.paymentService.WebBillingSessionResponse>(
                urls.api.payment.webBillingSession,
                {
                    body: request,
                },
            );
            return response.url;
        },
        onSuccess: (data) => {
            onReceiveBillingPortalSessionUrl?.(data as string);
        },
        onError: (error) => {
            console.error(error);
            notify('error', intl('errorCreatingBillingPortalSession'));
        },
    });

    const validateRegistrationCodeQuery = useQuery({
        queryKey: getRegistrationCodeValidationCacheKey(registrationCodeValidationInput || ''),
        queryFn: async () => {
            const response = await appAPI.post<APITypes.paymentService.ValidateRegistrationCodeResponse>(
                urls.api.payment.validateRegistrationCode,
                {
                    body: {
                        code: registrationCodeValidationInput,
                    },
                },
            );
            return response;
        },
        enabled: !!registrationCodeValidationInput,
    });
    useQueryErrorNotify(validateRegistrationCodeQuery, intl('errorValidatingPromoCode'));

    const applyRegistrationCode = useMutation({
        mutationFn: async (request: APITypes.paymentService.ApplyRegistrationCodeRequest) => {
            await appAPI.post<APITypes.paymentService.ApplyRegistrationCodeResponse>(
                urls.api.payment.applyRegistrationCode,
                {
                    body: request,
                },
            );
        },
        onSuccess: (_, args) => {
            onResetPaymentStatus.resetCache({})();
            onRegistrationCodeApplied?.(args.code);
        },
        onError: (error) => {
            console.error(error);
            notify('error', intl('errorApplyingPromoCode'));
        },
    });

    const autoApplyRegistrationCode = useMutation({
        mutationFn: async (request: APITypes.paymentService.ApplyRegistrationCodeRequest) => {
            try {
                await appAPI.post<APITypes.paymentService.ApplyRegistrationCodeResponse>(
                    urls.api.payment.applyRegistrationCode,
                    {
                        body: request,
                    },
                );
                return 'autoApplyRegistrationCodeSuccess';
            } catch (error) {
                // this is semi-expected if the link is old and the code is no longer valid.  Don't do the default auto-retry behavior
                console.error(error);
                return 'autoApplyRegistrationCodeError';
            }
        },
        onSuccess: (data, args) => {
            if (data === 'autoApplyRegistrationCodeSuccess') {
                onResetPaymentStatus.resetCache({})();
                onAutoApplyRegistrationCodeSuccess?.(args.code);
            } else {
                notify('error', intl('autoApplyRegistrationCodeFailure'));
            }
        },
        onError: (error) => {
            console.error(error);
            notify('error', intl('autoApplyRegistrationCodeFailure'));
        },
    });

    const myRegistrationCodeStatusQuery = useQuery({
        queryKey: getMyRegistrationCodeCacheKey(),
        queryFn: async () => {
            const response = await appAPI.get<APITypes.paymentService.UserRegistrationCodeStatus>(
                urls.api.payment.myRegistrationCode,
            );
            return response;
        },
        enabled: enableQueries,
    });
    useQueryErrorNotify(myRegistrationCodeStatusQuery, intl('errorLoadingRegistrationCodeStatus'));

    const deleteRegistrationCodeMutation = useMutation({
        mutationFn: async () => {
            await appAPI.del(urls.api.payment.registrationCode);
        },
        onSuccess: () => {
            onResetPaymentStatus.resetCache({})();
            notify('success', intl('successDeletingRegistrationCode'));
        },
        onError: (error) => {
            console.error(error);
            notify('error', intl('errorDeletingRegistrationCode'));
        },
    });

    const webProductsQuery = useQuery({
        queryKey: getWebProductsCacheKey(),
        queryFn: async () => {
            const response = await appAPI.get<APITypes.paymentService.WebProductsResponse>(
                urls.api.payment.webProducts,
            );
            return response;
        },
        enabled: enableQueries,
    });
    useQueryErrorNotify(webProductsQuery, intl('errorLoadingProducts'));

    const paymentStatusQuery = useQuery({
        queryKey: getPaymentStatusCacheKey(),
        queryFn: async () => {
            const response = await appAPI.get<APITypes.paymentService.PaymentStatusResponse>(
                urls.api.payment.paymentStatus,
            );
            return response;
        },
        enabled: enableQueries,
    });
    useQueryErrorNotify(paymentStatusQuery, intl('errorLoadingPaymentStatus'));
    useQueryCallbacks({
        query: paymentStatusQuery,
        onSuccess: (data) => {
            if (pollForPaymentStatus && paymentStatusPollCount < 5 && !data.entitlements?.length) {
                setTimeout(() => {
                    setPaymentStatusPollCount(paymentStatusPollCount + 1);
                    paymentStatusQuery.refetch();
                }, 1000);
            }
        },
    });

    const myMostPermissiveEntitlement = useMemo(() => {
        return paymentStatusQuery.data?.entitlements.includes('Family Plan') ? 'Family Plan' : null;
    }, [paymentStatusQuery.data?.entitlements]);
    const isLoadingPaymentStatus =
        paymentStatusQuery.isLoading ||
        (!myMostPermissiveEntitlement && paymentStatusPollCount < 5 && pollForPaymentStatus);

    return {
        isLoadingConnectUrl: createCheckoutSessionMutation.isPending,
        createWebCheckoutSession: createCheckoutSessionMutation.mutate,
        isLoadingBillingPortalUrl: createBillingPortalSessionMutation.isPending,
        createWebBillingPortalSession: createBillingPortalSessionMutation.mutate,
        isLoadingWebProducts: webProductsQuery.isLoading,
        paymentStatus: paymentStatusQuery.data || null,
        resetPaymentStatus: () => {
            onResetPaymentStatus.resetCache({})();
        },
        myMostPermissiveEntitlement: myMostPermissiveEntitlement,
        isLoadingPaymentStatus: !!isLoadingPaymentStatus,
        isValidatingRegistrationCode: validateRegistrationCodeQuery.isLoading,
        registrationCodeValidationResult: validateRegistrationCodeQuery.data || null,
        webProducts: webProductsQuery.data?.products ?? [],
        applyRegistrationCode: applyRegistrationCode.mutate,
        isApplyingRegistrationCode: applyRegistrationCode.isPending,
        myRegistrationCodeStatus: myRegistrationCodeStatusQuery.data || null,
        isLoadingRegistrationCodeStatus: myRegistrationCodeStatusQuery.isLoading,
        deleteRegistrationCode: deleteRegistrationCodeMutation.mutate,
        isDeletingRegistrationCode: deleteRegistrationCodeMutation.isPending,
        autoApplyRegistrationCode: autoApplyRegistrationCode.mutate,
        isAutoApplyingRegistrationCode: autoApplyRegistrationCode.isPending,
        purchaseNativeProduct: purchaseNativeProductMutation.mutate,
        isPurchasingNativeProduct: purchaseNativeProductMutation.isPending,
        createNativeProductManagementSession: createNativeProductManagementSessionMutation.mutate,
        isLoadingNativeProductManagementSession: createNativeProductManagementSessionMutation.isPending,
        isDelayingForNativeWebhookPropagation: isAwaitingNativeWebhookPropagation,
    };
}
